import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Gift, Flame, Clock, Percent, Star, Image as ImageIcon, Loader2, Tag, DollarSign, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardFooter } from '../../components/ui/card';

const AdminDeals = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalPrice: '',
    dealPrice: '',
    validity: '',
    category: 'combo',
    items: '',
    isActive: true,
    icon: 'fire',
    color: 'red',
    image: '',
    additional_images: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const iconOptions = [
    { value: 'fire', icon: Flame, label: 'Fire' },
    { value: 'gift', icon: Gift, label: 'Gift' },
    { value: 'star', icon: Star, label: 'Star' },
    { value: 'clock', icon: Clock, label: 'Clock' },
    { value: 'percent', icon: Percent, label: 'Percent' }
  ];

  const colorOptions = [
    { value: 'red', label: 'Red', class: 'from-red-900/40 to-red-700/20 border-red-500/50 text-red-400' },
    { value: 'purple', label: 'Purple', class: 'from-purple-900/40 to-purple-700/20 border-purple-500/50 text-purple-400' },
    { value: 'gold', label: 'Gold', class: 'from-gold-900/40 to-gold-700/20 border-gold-500/50 text-gold-400' },
    { value: 'blue', label: 'Blue', class: 'from-blue-900/40 to-blue-700/20 border-blue-500/50 text-blue-400' },
    { value: 'green', label: 'Green', class: 'from-green-900/40 to-green-700/20 border-green-500/50 text-green-400' },
    { value: 'orange', label: 'Orange', class: 'from-orange-900/40 to-orange-700/20 border-orange-500/50 text-orange-400' }
  ];

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchDeals();
  }, [admin, navigate]);

  const fetchDeals = async () => {
    try {
      const response = await axios.get('/deals');
      setDeals(response.data);
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching deals:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    const formDataUpload = new FormData();
    formDataUpload.append('image', imageFile);
    try {
      const response = await axios.post('/menu/upload-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.imageUrl;
    } catch (error) {
      logger.error('Error uploading image:', error);
      throw error;
    }
  };

  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImageFiles(prev => [...prev, ...files]);

      const newPreviews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAdditionalImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));

    // Also remove from formData if it was an existing URL
    if (index < (formData.additional_images?.length || 0) && typeof formData.additional_images[index] === 'string') {
      const updatedImages = [...formData.additional_images];
      updatedImages.splice(index, 1);
      setFormData(prev => ({ ...prev, additional_images: updatedImages }));
    }
  };

  const uploadAdditionalImages = async () => {
    if (additionalImageFiles.length === 0) return [];

    const uploadedUrls = [];
    for (const file of additionalImageFiles) {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      try {
        const response = await axios.post('/menu/upload-image', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(response.data.imageUrl);
      } catch (error) {
        logger.error('Error uploading additional image:', error);
      }
    }
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const newAdditionalImages = await uploadAdditionalImages();
      // Combine existing images (that represent URLs) with newly uploaded ones
      const finalAdditionalImages = [
        ...(formData.additional_images || []).filter(img => typeof img === 'string'),
        ...newAdditionalImages
      ];

      const data = {
        ...formData,
        originalPrice: Number(formData.originalPrice),
        dealPrice: Number(formData.dealPrice),
        image: imageUrl || '',
        additional_images: finalAdditionalImages
      };

      if (editingDeal) {
        await axios.put(`/deals/${editingDeal.id || editingDeal._id}`, data);
      } else {
        await axios.post('/deals', data);
      }

      fetchDeals();
      resetForm();
      setShowModal(false);
    } catch (error) {
      logger.error('Error saving deal:', error);
      alert('Failed to save deal. Please try again.');
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title,
      description: deal.description,
      originalPrice: deal.original_price, // Map from DB field
      dealPrice: deal.deal_price,         // Map from DB field
      validity: deal.validity,
      category: deal.category || 'combo',
      items: deal.items_json || '',       // Map from DB field
      isActive: deal.isActive,
      icon: deal.icon || 'fire',
      color: deal.color || 'red',
      image: deal.image || '',
      additional_images: deal.additional_images || []
    });
    setImageFile(null);
    setImagePreview(deal.image || '');
    setAdditionalImageFiles([]);
    setAdditionalImagePreviews(deal.additional_images || []);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      try {
        await axios.delete(`/deals/${id}`);
        fetchDeals();
      } catch (error) {
        logger.error('Error deleting deal:', error);
        alert('Failed to delete deal.');
      }
    }
  };

  const toggleActive = async (deal) => {
    try {
      await axios.put(`/deals/${deal.id || deal._id}`, {
        ...deal,
        isActive: !deal.isActive
      });
      fetchDeals();
    } catch (error) {
      logger.error('Error toggling deal status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      originalPrice: '',
      dealPrice: '',
      validity: '',
      category: 'combo',
      items: '',
      isActive: true,
      icon: 'fire',
      color: 'red',
      image: '',
      additional_images: []
    });
    setEditingDeal(null);
    setImageFile(null);
    setImagePreview('');
    setAdditionalImageFiles([]);
    setAdditionalImagePreviews([]);
  };

  const getIconComponent = (iconType) => {
    const iconMap = {
      fire: Flame,
      gift: Gift,
      star: Star,
      clock: Clock,
      percent: Percent
    };
    const Icon = iconMap[iconType] || Flame;
    return <Icon className="w-8 h-8" />;
  };

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />

      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Deals Management
            </h1>
            <p className="text-gray-400 mt-2">Create and manage special offers and combos</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gold-500 text-black hover:bg-gold-400 font-bold px-6 h-12 rounded-full shadow-lg shadow-gold-500/20 transition-all hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Deal
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {deals.map((deal, index) => (
              <motion.div
                key={deal.id || deal._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`overflow-hidden h-full flex flex-col bg-black/40 backdrop-blur-sm border transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/5 ${deal.isActive ? 'border-gold-500/20 hover:border-gold-500/50' : 'border-gray-800 opacity-70'}`}>
                  <div className="relative h-48 overflow-hidden bg-gray-900">
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10 opacity-60" />
                    {deal.image ? (
                      <img
                        src={deal.image}
                        alt={deal.title}
                        className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-br ${colorOptions.find(c => c.value === deal.color)?.class.replace('border-', '').replace('text-', '')}`}>
                        <div className={`text-${deal.color}-400 mb-2`}>{getIconComponent(deal.icon)}</div>
                        <p className="text-gray-500 text-sm">No Image</p>
                      </div>
                    )}

                    <div className="absolute top-3 right-3 z-20">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 ${deal.isActive ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                        {deal.isActive ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                        {deal.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 z-20">
                      <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-medium">
                        {deal.category}
                      </span>
                    </div>
                  </div>

                  <CardContent className="p-5 flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-gold-200 line-clamp-1 font-serif">{deal.title}</h3>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-3">{deal.description}</p>

                    {deal.items_json && (
                      <div className="flex items-start gap-2 mb-4 bg-white/5 p-2 rounded text-xs text-gray-300">
                        <Gift className="w-3 h-3 mt-0.5 text-gold-500" />
                        <span className="line-clamp-2">{deal.items_json}</span>
                      </div>
                    )}

                    <div className="flex items-end justify-between mb-4 mt-auto">
                      <div>
                        <p className="text-gray-500 text-xs line-through">Rs. {deal.original_price?.toLocaleString()}</p>
                        <p className="text-gold-400 font-bold text-xl">Rs. {deal.deal_price?.toLocaleString()}</p>
                      </div>
                      <div className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded border border-red-500/20 font-bold">
                        Save {Math.round(((deal.original_price - deal.deal_price) / deal.original_price) * 100)}%
                      </div>
                    </div>

                    <p className="text-gray-500 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {deal.validity}
                    </p>
                  </CardContent>

                  <CardFooter className="p-4 pt-0 grid grid-cols-2 gap-2 mt-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(deal)}
                      className="border-blue-500/20 text-blue-400 hover:bg-blue-600 hover:text-white hover:border-transparent"
                    >
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(deal)}
                      className={deal.isActive ? "border-amber-500/20 text-amber-400 hover:bg-amber-600 hover:text-white hover:border-transparent" : "border-green-500/20 text-green-400 hover:bg-green-600 hover:text-white hover:border-transparent"}
                    >
                      {deal.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(deal.id || deal._id)}
                      className="col-span-2 border-red-500/20 text-red-400 hover:bg-red-600 hover:text-white hover:border-transparent"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete Deal
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}

            {deals.length === 0 && (
              <div className="col-span-full py-20 text-center flex flex-col items-center justify-center text-gray-500">
                <Gift className="w-16 h-16 text-gray-700 mb-4" />
                <p className="text-xl">No active deals found</p>
                <p className="text-sm">Create a new deal to get started</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { setShowModal(false); resetForm(); }}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="relative bg-gray-900 border border-gold-500/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
              >
                <div className="flex justify-between items-center p-6 border-b border-white/10 bg-black/40">
                  <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
                    {editingDeal ? 'Edit Deal' : 'New Super Deal'}
                  </h2>
                  <Button variant="ghost" size="icon" onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title & Description */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400">Deal Title</label>
                        <Input
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g., Weekend Family Feast"
                          className="bg-black/20 border-white/10 focus:border-gold-500/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400">Description</label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          rows="3"
                          className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                          placeholder="Get 20% off on all family platters..."
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400">Items Included</label>
                        <Input
                          name="items"
                          value={formData.items}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g., 2 Large Pizzas, 1 Drink"
                          className="bg-black/20 border-white/10 focus:border-gold-500/50"
                        />
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Original Price
                        </label>
                        <Input
                          type="number"
                          name="originalPrice"
                          value={formData.originalPrice}
                          onChange={handleInputChange}
                          required
                          min="0"
                          className="bg-black/20 border-white/10 focus:border-gold-500/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                          <Tag className="w-4 h-4" /> Deal Price
                        </label>
                        <Input
                          type="number"
                          name="dealPrice"
                          value={formData.dealPrice}
                          onChange={handleInputChange}
                          required
                          min="0"
                          className="bg-black/20 border-white/10 focus:border-gold-500/50"
                        />
                      </div>
                    </div>

                    {formData.originalPrice && formData.dealPrice && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                        <p className="text-green-400 font-bold text-sm">
                          You're offering a {Math.round(((formData.originalPrice - formData.dealPrice) / formData.originalPrice) * 100)}% discount!
                        </p>
                      </div>
                    )}

                    {/* Category & Validity */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400">Category</label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className="w-full h-10 rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                        >
                          <option value="combo">Combo Deal</option>
                          <option value="family">Family Pack</option>
                          <option value="special">Special Offer</option>
                          <option value="seasonal">Seasonal</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gold-400">Validity</label>
                        <Input
                          name="validity"
                          value={formData.validity}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g., Valid until Dec 31st"
                          className="bg-black/20 border-white/10 focus:border-gold-500/50"
                        />
                      </div>
                    </div>

                    {/* Icon & Color (Visuals) */}
                    <div className="space-y-4">
                      <label className="text-sm font-medium text-gold-400">Visual Theme</label>
                      <div className="flex gap-4 overflow-x-auto pb-2 custom-scrollbar">
                        {iconOptions.map((opt) => {
                          const Icon = opt.icon;
                          return (
                            <div
                              key={opt.value}
                              onClick={() => setFormData({ ...formData, icon: opt.value })}
                              className={`cursor-pointer p-3 rounded-lg border flex flex-col items-center gap-2 min-w-[80px] transition-all ${formData.icon === opt.value ? 'bg-gold-500/20 border-gold-500 text-gold-400' : 'bg-white/5 border-white/10 text-gray-500 hover:bg-white/10'}`}
                            >
                              <Icon className="w-6 h-6" />
                              <span className="text-xs">{opt.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                      <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" /> Deal Cover Image
                      </label>
                      <div className="flex items-start gap-4">
                        {imagePreview && (
                          <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-gold-500/30" />
                        )}
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="bg-transparent border-white/10 cursor-pointer file:bg-gold-500/10 file:text-gold-500 file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-4 file:font-semibold hover:file:bg-gold-500/20"
                        />
                      </div>
                    </div>

                    {/* Additional Images Upload */}
                    <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                      <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" /> Additional Images (Gallery)
                      </label>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImagesChange}
                        className="bg-transparent border-white/10 cursor-pointer file:bg-gold-500/10 file:text-gold-500 file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-4 file:font-semibold hover:file:bg-gold-500/20"
                      />

                      {additionalImagePreviews.length > 0 && (
                        <div className="grid grid-cols-4 gap-4 mt-2">
                          {additionalImagePreviews.map((preview, index) => (
                            <div key={index} className="relative group">
                              <img src={preview} alt={`Additional ${index}`} className="w-full h-20 object-cover rounded-lg border border-white/10" />
                              <button
                                type="button"
                                onClick={() => removeAdditionalImage(index)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center justify-between bg-black/20 p-4 rounded-lg border border-white/10">
                      <span className="text-white font-medium">Active Status</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold-500"></div>
                      </label>
                    </div>

                    <div className="flex gap-4 pt-4 border-t border-white/10">
                      <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 bg-gradient-to-r from-gold-600 to-gold-500 text-black font-bold hover:shadow-lg hover:shadow-gold-500/20">
                        {editingDeal ? 'Save Changes' : 'Create Deal'}
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDeals;


